import { useState, KeyboardEvent, useRef, useEffect, useCallback, useMemo } from "react";
import { ArrowUp, X, MapPin, Loader2, Paperclip, ImagePlus, FileStack, FileText, ClipboardList, BookOpen, Camera, RefreshCw, type LucideIcon } from "lucide-react";
import sparklesIcon from "@/assets/sparkles-icon.png";
import { trackPromptAdded, trackAttachmentUploadedV2 } from "@/lib/analytics";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { COUNTRIES } from "@/lib/countries";
import { cn } from "@/lib/utils";
import { useLocationDetection } from "@/hooks/use-location-detection";
import { useTypingAnimation } from "@/hooks/use-typing-animation";
import { GoogleIcon, OpenAIIcon } from "@/components/ModelProviderIcons";
import { toast } from "@/hooks/use-toast";
import { FormatSelector, ExportFormat } from "@/components/FormatSelector";
const LLM_MODELS = [{
  value: "google/gemini-3-flash-preview",
  label: "Gemini 3 Flash",
  description: "Fast & Balanced",
  provider: "google"
}, {
  value: "google/gemini-2.5-flash-lite",
  label: "Gemini Lite",
  description: "Fastest",
  provider: "google"
}, {
  value: "openai/gpt-5-mini",
  label: "GPT-5 Mini",
  description: "Fast & Smart",
  provider: "openai"
}] as const;
const DEFAULT_MAX_PHOTOS = 10;
const DEFAULT_MAX_PLANS = 5;
const DEFAULT_MAX_DOCS = 5;
const MIN_PROMPT_LENGTH = 25;
const DEFAULT_MAX_PROMPT_LENGTH = 2000;
const ACCEPTED_PHOTO_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const ACCEPTED_PLAN_TYPES = ["application/pdf"];
const ACCEPTED_DOC_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const ACCEPTED_DOC_EXTENSIONS = [".pdf", ".docx", ".xlsx"];
const DEFAULT_EXAMPLE_PROMPTS = ["Describe the work activity...", "Operating a 6m electric scissor lift indoors to install overhead lighting in a warehouse.", "Excavating a 1.2m deep trench for laying new electrical conduits along a footpath.", "Using a 125mm angle grinder to cut 50x50 SHS steel sections in a fabrication workshop."];
const DEFAULT_CLICKABLE_PROMPTS = ["\"Concrete cutting for a job where a defective slab on the fourth floor needs to be removed, and asbestos is present\"", "\"Excavating around a jemena pit, with another endeavour pit nearby, on the side of a riverbank\"", "\"Installing new overhead cables on a transgrid job near Bega in July\""];
interface PhotoFile {
  file: File;
  preview: string;
  id: string;
}
interface PlanFile {
  file: File;
  name: string;
  id: string;
}
interface DocFile {
  file: File;
  name: string;
  id: string;
}
interface AttachmentCategoryProp {
  key: string;
  kind: "photo" | "plan" | "doc";
  label: string;
  helper?: string;
  icon?: "ImagePlus" | "Camera" | "FileStack" | "FileText" | "ClipboardList" | "BookOpen" | "Wrench";
}
interface AttachmentConfigProp {
  enabled: boolean;
  cta_label: string;
  cta_short_label: string;
  helper_text: string;
  suggested_categories: string[];
  max_files: number;
  max_file_mb: number;
  max_photos?: number;
  max_plans?: number;
  max_docs?: number;
  categories?: AttachmentCategoryProp[];
}
const ICON_MAP: Record<NonNullable<AttachmentCategoryProp["icon"]>, LucideIcon> = {
  ImagePlus,
  Camera,
  FileStack,
  FileText,
  ClipboardList,
  BookOpen,
  Wrench: Paperclip,
};
interface ChatInterfaceProps {
  placeholder: string;
  onSubmit: (data: {
    activity: string;
    country: string;
    state: string;
    model: string;
    photos: string[];
    plans: string[];
    /** Doc files (PDF/DOCX/XLSX) as base64 — separate from plans so backend can branch by kind. */
    docs?: string[];
    /** Per-kind file names aligned with the data arrays — used for analyze-attachments traceability. */
    photoNames?: string[];
    planNames?: string[];
    docNames?: string[];
    exportFormat: ExportFormat;
  }) => void;
  isProcessing: boolean;
  isValidating?: boolean;
  showExamplePrompts?: boolean;
  examplePrompts?: string[];
  supportedExportFormats?: string[];
  /** Supported country codes/names from generator config. Supports "GLOBAL" for all countries. */
  supportedCountries?: string[];
  /** Initial country code to preserve selection across resets */
  initialCountry?: string;
  /** Initial state code to preserve selection across resets */
  initialState?: string;
  /** Compact mode for split-preview variant — removes max-width and reduces height */
  compact?: boolean;
  /** Generator display name for compact banner header */
  generatorDisplayName?: string;
  /** Descriptor text for compact banner header */
  bannerDescriptor?: string;
  /** Per-generator attachments config (gates CTA + drives copy/limits) */
  attachmentConfig?: AttachmentConfigProp;
  /** Generator slug — required for analytics events */
  generatorSlug?: string;
  /** A/B variant key (A = stacked, B = split-preview) for analytics */
  variantKey?: string | null;
  /** Per-generator max prompt length from DB (fallback 2000) */
  maxPromptLength?: number;
}
export function ChatInterface({
  placeholder,
  onSubmit,
  isProcessing,
  isValidating = false,
  showExamplePrompts = false,
  examplePrompts = [],
  supportedExportFormats = ["docx"],
  supportedCountries,
  initialCountry,
  initialState,
  compact = false,
  generatorDisplayName,
  bannerDescriptor,
  attachmentConfig,
  generatorSlug,
  variantKey,
  maxPromptLength
}: ChatInterfaceProps) {
  const effectiveMaxLength = maxPromptLength || DEFAULT_MAX_PROMPT_LENGTH;

  // Resolve attachment config (back-compat default keeps universal Tools CTA)
  const attach = attachmentConfig ?? {
    enabled: true,
    cta_label: "Tools",
    cta_short_label: "Tools",
    helper_text: "Add supporting files to give the AI more context.",
    suggested_categories: [],
    max_files: DEFAULT_MAX_PHOTOS + DEFAULT_MAX_PLANS + DEFAULT_MAX_DOCS,
    max_file_mb: 10,
    categories: [] as never[],
    max_photos: DEFAULT_MAX_PHOTOS,
    max_plans: DEFAULT_MAX_PLANS,
    max_docs: DEFAULT_MAX_DOCS,
  };
  // Per-kind ceilings (DB-driven, fall back to sensible defaults)
  const MAX_PHOTOS = (attach as { max_photos?: number }).max_photos ?? DEFAULT_MAX_PHOTOS;
  const MAX_PLANS = (attach as { max_plans?: number }).max_plans ?? DEFAULT_MAX_PLANS;
  const MAX_DOCS = (attach as { max_docs?: number }).max_docs ?? DEFAULT_MAX_DOCS;
  // Use provided example prompts or fall back to defaults
  const EXAMPLE_PROMPTS = useMemo(() =>
  examplePrompts.length > 0 ?
  [placeholder, ...examplePrompts] :
  DEFAULT_EXAMPLE_PROMPTS,
  [placeholder, examplePrompts]
  );
  const CLICKABLE_EXAMPLE_PROMPTS = useMemo(() =>
  examplePrompts.length > 0 ?
  examplePrompts.map((p) => `"${p}"`) :
  DEFAULT_CLICKABLE_PROMPTS,
  [examplePrompts]
  );

  // Dynamic country list based on generator config
  // Strip "GLOBAL" sentinel before filtering — it's an API-only fallback marker
  const SUPPORTED_COUNTRIES = useMemo(() => {
    if (!supportedCountries?.length) {
      return COUNTRIES.filter((c) => c.code !== "OTHER");
    }
    const codes = supportedCountries.filter((c) => c !== "GLOBAL");
    if (codes.length === 0) {
      return COUNTRIES.filter((c) => c.code !== "OTHER");
    }
    return COUNTRIES.filter((c) => codes.includes(c.code) || codes.includes(c.name));
  }, [supportedCountries]);

  // Pass supported countries to location detection for consistent filtering
  // Skip detection if initial values are provided (preserving selection on reset)
  const detectedLocation = useLocationDetection(supportedCountries);
  const skipLocationDetection = Boolean(initialCountry);
  const {
    text: animatedPlaceholder,
    isFadingIn
  } = useTypingAnimation(EXAMPLE_PROMPTS, 30, 2000);
  const [activity, setActivity] = useState("");
  const [country, setCountry] = useState(initialCountry || "");
  const [state, setState] = useState(initialState || "");
  const [model, setModel] = useState("google/gemini-3-flash-preview");
  const [hasInitialized, setHasInitialized] = useState(skipLocationDetection);
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const [plans, setPlans] = useState<PlanFile[]>([]);
  const [docs, setDocs] = useState<DocFile[]>([]);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [descriptionError, setDescriptionError] = useState("");
  const [currentExampleIndex, setCurrentExampleIndex] = useState(() =>
  Math.floor(Math.random() * CLICKABLE_EXAMPLE_PROMPTS.length)
  );
  const [isExampleTransitioning, setIsExampleTransitioning] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>(() => {
    // Default to first supported format
    if (supportedExportFormats.includes("xlsx")) return "xlsx";
    return "docx";
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const planInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const selectedModel = LLM_MODELS.find((m) => m.value === model);

  // Initialize location from detection once loaded (skip if initial values provided)
  useEffect(() => {
    if (!detectedLocation.isLoading && !hasInitialized && !skipLocationDetection) {
      // Only set detected country if it's in the supported list
      const isSupported = SUPPORTED_COUNTRIES.some((c) => c.code === detectedLocation.countryCode);
      if (isSupported) {
        setCountry(detectedLocation.countryCode);
        setState(detectedLocation.stateCode);
      }
      setHasInitialized(true);
    }
  }, [detectedLocation.isLoading, detectedLocation.countryCode, detectedLocation.stateCode, hasInitialized, skipLocationDetection, SUPPORTED_COUNTRIES]);
  const selectedCountry = country ? SUPPORTED_COUNTRIES.find((c) => c.code === country) : null;
  const compressImage = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            reject(new Error("Could not get canvas context"));
            return;
          }

          // Target max dimension of 1024px for reasonable quality/size balance
          const maxDim = 1024;
          let width = img.width;
          let height = img.height;
          if (width > height && width > maxDim) {
            height = height * maxDim / width;
            width = maxDim;
          } else if (height > maxDim) {
            width = width * maxDim / height;
            height = maxDim;
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Compress to JPEG at 0.7 quality
          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error("Failed to load image"));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Per-generator combined cap (photos + plans + docs) takes precedence over MAX_PHOTOS
    const combinedRemaining = Math.max(0, attach.max_files - photos.length - plans.length - docs.length);
    const photoSlotRemaining = MAX_PHOTOS - photos.length;
    const remaining = Math.min(combinedRemaining, photoSlotRemaining);
    const toAdd = files.slice(0, remaining);
    if (files.length > toAdd.length) {
      toast({
        title: "Attachment limit reached",
        description: `You can attach up to ${attach.max_files} files for this generator.`,
        variant: "destructive"
      });
    }
    let added = 0;
    for (const file of toAdd) {
      if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `"${file.name}" is not a supported image format. Please use PNG, JPG, or WebP.`,
          variant: "destructive"
        });
        continue;
      }
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > attach.max_file_mb) {
        toast({
          title: "File too large",
          description: `"${file.name}" is ${sizeMb.toFixed(1)}MB. Max ${attach.max_file_mb}MB per file.`,
          variant: "destructive"
        });
        continue;
      }
      try {
        const preview = await compressImage(file);
        setPhotos((prev) => [...prev, {
          file,
          preview,
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        }]);
        added += 1;
      } catch (err) {
        console.error("Failed to process image:", err);
      }
    }
    if (added > 0 && generatorSlug) {
      trackAttachmentUploadedV2({
        generatorSlug,
        fileKind: "photo",
        fileCount: added,
        totalAttachments: photos.length + plans.length + docs.length + added,
        surface: "web",
        variantKey: variantKey ?? null
      });
    }
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
    setToolsOpen(false);
  };
  const handlePlanSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const combinedRemaining = Math.max(0, attach.max_files - photos.length - plans.length - docs.length);
    const planSlotRemaining = MAX_PLANS - plans.length;
    const remaining = Math.min(combinedRemaining, planSlotRemaining);
    const toAdd = files.slice(0, remaining);
    if (files.length > toAdd.length) {
      toast({
        title: "Attachment limit reached",
        description: `You can attach up to ${attach.max_files} files for this generator.`,
        variant: "destructive"
      });
    }
    let added = 0;
    for (const file of toAdd) {
      if (!ACCEPTED_PLAN_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: `"${file.name}" is not a PDF. Please upload PDF files only.`,
          variant: "destructive"
        });
        continue;
      }
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > attach.max_file_mb) {
        toast({
          title: "File too large",
          description: `"${file.name}" is ${sizeMb.toFixed(1)}MB. Max ${attach.max_file_mb}MB per file.`,
          variant: "destructive"
        });
        continue;
      }
      setPlans((prev) => [...prev, {
        file,
        name: file.name,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }]);
      added += 1;
    }
    if (added > 0 && generatorSlug) {
      trackAttachmentUploadedV2({
        generatorSlug,
        fileKind: "plan",
        fileCount: added,
        totalAttachments: photos.length + plans.length + docs.length + added,
        surface: "web",
        variantKey: variantKey ?? null
      });
    }
    if (planInputRef.current) {
      planInputRef.current.value = "";
    }
    setToolsOpen(false);
  };
  const handleDocSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const combinedRemaining = Math.max(0, attach.max_files - photos.length - plans.length - docs.length);
    const docSlotRemaining = MAX_DOCS - docs.length;
    const remaining = Math.min(combinedRemaining, docSlotRemaining);
    const toAdd = files.slice(0, remaining);
    if (files.length > toAdd.length) {
      toast({
        title: "Attachment limit reached",
        description: `You can attach up to ${attach.max_files} files for this generator.`,
        variant: "destructive"
      });
    }
    let added = 0;
    for (const file of toAdd) {
      const lowerName = file.name.toLowerCase();
      const extOk = ACCEPTED_DOC_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
      const mimeOk = ACCEPTED_DOC_TYPES.includes(file.type);
      if (!extOk && !mimeOk) {
        toast({
          title: "Invalid file type",
          description: `"${file.name}" is not a supported document. Use PDF, DOCX, or XLSX.`,
          variant: "destructive"
        });
        continue;
      }
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > attach.max_file_mb) {
        toast({
          title: "File too large",
          description: `"${file.name}" is ${sizeMb.toFixed(1)}MB. Max ${attach.max_file_mb}MB per file.`,
          variant: "destructive"
        });
        continue;
      }
      setDocs((prev) => [...prev, {
        file,
        name: file.name,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }]);
      added += 1;
    }
    if (added > 0 && generatorSlug) {
      trackAttachmentUploadedV2({
        generatorSlug,
        fileKind: "plan",
        fileCount: added,
        totalAttachments: photos.length + plans.length + docs.length + added,
        surface: "web",
        variantKey: variantKey ?? null
      });
    }
    if (docInputRef.current) {
      docInputRef.current.value = "";
    }
    setToolsOpen(false);
  };
  const removePhoto = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };
  const removePlan = (id: string) => {
    setPlans((prev) => prev.filter((p) => p.id !== id));
  };
  const removeDoc = (id: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  };
  const convertPlanToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };
  const handleSubmit = async () => {
    const trimmedActivity = activity.trim();

    // Check if prompt and/or country is empty
    if (!trimmedActivity && !country) {
      setDescriptionError("Please enter a suitable prompt and select a country.");
      return;
    }
    if (!trimmedActivity) {
      setDescriptionError("Please enter a suitable prompt");
      return;
    }
    if (!country) {
      setDescriptionError("Please select a country");
      return;
    }

    // Validate prompt length
    if (trimmedActivity.length < MIN_PROMPT_LENGTH) {
      setDescriptionError(`Please provide at least ${MIN_PROMPT_LENGTH} characters. Alternatively, you can try clicking an example prompt below.`);
      return;
    }
    if (trimmedActivity.length > effectiveMaxLength) {
      toast({
        title: "Prompt too long",
        description: `Please keep your description under ${effectiveMaxLength} characters. Current: ${trimmedActivity.length}`,
        variant: "destructive"
      });
      return;
    }
    const planBase64s = await Promise.all(plans.map((p) => convertPlanToBase64(p.file)));
    const docBase64s = await Promise.all(docs.map((d) => convertPlanToBase64(d.file)));

    // Convert country/state codes to full names for edge function compatibility
    const countryName = SUPPORTED_COUNTRIES.find((c) => c.code === country)?.name || country;
    const stateName = selectedCountry?.states?.find((s) => s.code === state)?.name || state;

    onSubmit({
      activity: trimmedActivity,
      country: countryName,
      state: stateName,
      model,
      photos: photos.map((p) => p.preview),
      plans: planBase64s,
      docs: docBase64s,
      photoNames: photos.map((p) => p.file.name),
      planNames: plans.map((p) => p.name),
      docNames: docs.map((d) => d.name),
      exportFormat
    });
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const clearLocation = () => {
    setCountry("");
    setState("");
  };

  const handleSeeAnother = useCallback(() => {
    setIsExampleTransitioning(true);
    setTimeout(() => {
      setCurrentExampleIndex((prev) => (prev + 1) % CLICKABLE_EXAMPLE_PROMPTS.length);
      setIsExampleTransitioning(false);
    }, 150);
  }, []);

  // Pill style: white bg + border in compact mode, muted in default
  const pillClass = compact ?
  "bg-card border border-border hover:bg-muted/50" :
  "bg-muted/50 hover:bg-muted";

  // Shared toolbar content (location, tools, model, format selectors)
  const toolbarContent =
  <>
      {detectedLocation.isLoading ? <div className={cn("flex items-center gap-2 rounded-full px-3 h-9 text-sm", pillClass)}>
          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="flex items-center gap-2 min-w-[120px]">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Detecting...</span>
          </div>
        </div> : <Select value={country} onValueChange={(value) => {
      setCountry(value);
      setState("");
      if (descriptionError) setDescriptionError("");
    }}>
          <SelectTrigger className={cn("flex items-center gap-2 rounded-full px-3 h-9 text-sm transition-colors !ring-0 !ring-offset-0 w-auto", pillClass, compact ? "!border-border" : "!border-0", descriptionError && !country && "ring-2 ring-destructive bg-destructive/5")}>
            <MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent align="start">
            {SUPPORTED_COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>
                {c.name}
              </SelectItem>)}
          </SelectContent>
        </Select>}

      {selectedCountry?.states && !detectedLocation.isLoading && <Select value={state} onValueChange={setState}>
          <SelectTrigger className={cn("flex items-center gap-2 rounded-full px-3 h-9 text-sm transition-colors !ring-0 !ring-offset-0 w-auto", pillClass, compact ? "!border-border" : "!border-0")}>
            <SelectValue placeholder={selectedCountry.stateLabel || "State"} />
          </SelectTrigger>
          <SelectContent align="start">
            {selectedCountry.states.map((s) => <SelectItem key={s.code} value={s.code}>
                {s.name}
              </SelectItem>)}
          </SelectContent>
        </Select>}

      {country && !detectedLocation.isLoading && <button onClick={clearLocation} className="bg-muted hover:bg-muted/80 rounded-full h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" title="Clear location">
          <X className="h-3.5 w-3.5" />
        </button>}

      {/* Tools / Attachments pill — gated by attachmentConfig.enabled */}
      {attach.enabled && (
        <Popover open={toolsOpen} onOpenChange={setToolsOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "flex items-center gap-2 rounded-full px-3 h-9 text-sm transition-colors",
                pillClass,
                compact && "border border-border",
                (photos.length > 0 || plans.length > 0 || docs.length > 0) && "ring-1 ring-primary/50"
              )}
              disabled={isProcessing || isValidating}
              title={attach.cta_label}
            >
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="hidden sm:inline">{attach.cta_short_label}</span>
              {(photos.length > 0 || plans.length > 0 || docs.length > 0) && (
                <span className="bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center">
                  {photos.length + plans.length + docs.length}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start">
            <p className="px-3 pt-1 pb-2 text-xs text-muted-foreground">
              {attach.helper_text}
            </p>
            {attach.categories && attach.categories.length > 0 ? (
              attach.categories.map((cat) => {
                const used =
                  cat.kind === "photo" ? photos.length :
                  cat.kind === "plan" ? plans.length :
                  docs.length;
                const slotMax =
                  cat.kind === "photo" ? MAX_PHOTOS :
                  cat.kind === "plan" ? MAX_PLANS :
                  MAX_DOCS;
                const ref =
                  cat.kind === "photo" ? photoInputRef :
                  cat.kind === "plan" ? planInputRef :
                  docInputRef;
                const defaultIcon =
                  cat.kind === "photo" ? ImagePlus :
                  cat.kind === "plan" ? FileStack :
                  FileText;
                const Icon = cat.icon ? ICON_MAP[cat.icon] : defaultIcon;
                // Render label as "Label — italic helper" when helper provided
                return (
                  <button
                    key={cat.key}
                    onClick={() => ref.current?.click()}
                    disabled={used >= slotMax}
                    className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    <div className="text-left flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{cat.label}</span>
                        <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
                          {used}/{slotMax}
                        </span>
                      </div>
                      {cat.helper && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {cat.helper}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <>
                <button
                  onClick={() => photoInputRef.current?.click()}
                  disabled={photos.length >= MAX_PHOTOS}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ImagePlus className="h-4 w-4 text-muted-foreground" />
                  <div className="text-left">
                    <div className="font-medium">Attach site specific photos</div>
                    <div className="text-xs text-muted-foreground">
                      {photos.length}/{MAX_PHOTOS} added • PNG, JPG, WebP
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => planInputRef.current?.click()}
                  disabled={plans.length >= MAX_PLANS}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FileStack className="h-4 w-4 text-muted-foreground" />
                  <div className="text-left">
                    <div className="font-medium">Attach site specific plans</div>
                    <div className="text-xs text-muted-foreground">
                      {plans.length}/{MAX_PLANS} added • PDF only
                    </div>
                  </div>
                </button>
              </>
            )}
          </PopoverContent>
        </Popover>
      )}

      {(photos.length > 0 || plans.length > 0 || docs.length > 0) && <button onClick={() => {
      setPhotos([]);
      setPlans([]);
      setDocs([]);
    }} className="bg-muted hover:bg-muted/80 rounded-full h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors" title="Clear all attachments" disabled={isProcessing || isValidating}>
          <X className="h-3.5 w-3.5" />
        </button>}

      {/* Hidden file inputs */}
      <input ref={photoInputRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" multiple onChange={handlePhotoSelect} className="hidden" />
      <input ref={planInputRef} type="file" accept="application/pdf" multiple onChange={handlePlanSelect} className="hidden" />
      <input ref={docInputRef} type="file" accept=".pdf,.docx,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" multiple onChange={handleDocSelect} className="hidden" />

      {/* Model selector + Format selector */}
      <Select value={model} onValueChange={setModel} disabled={isProcessing || isValidating}>
        <SelectTrigger className={cn("rounded-full h-9 px-3 gap-1.5 text-sm focus:ring-0 w-auto", compact ? "" : "ml-auto", pillClass, compact ? "border-border" : "border-0")}>
          {selectedModel?.provider === "google" ? <GoogleIcon className="h-3.5 w-3.5" /> : <OpenAIIcon className="h-3.5 w-3.5" />}
          <span className="hidden sm:inline">{selectedModel?.label}</span>
        </SelectTrigger>
        <SelectContent>
          {LLM_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>
              <div className="flex items-center gap-2">
                {m.provider === "google" ? <GoogleIcon className="h-4 w-4" /> : <OpenAIIcon className="h-4 w-4" />}
                <span>{m.label}</span>
                <span className="text-muted-foreground text-xs">({m.description})</span>
              </div>
            </SelectItem>)}
        </SelectContent>
      </Select>

      <FormatSelector
      supportedFormats={supportedExportFormats}
      selectedFormat={exportFormat}
      onFormatChange={setExportFormat}
      disabled={isProcessing || isValidating}
      pillClassName={compact ? "bg-card border border-border hover:bg-muted/50" : undefined} />
    
    </>;


  return <div className={cn("w-full mx-auto", compact ? "px-0" : "max-w-4xl px-4")}>
      {compact ? (
    /* Split-view: single card */
    <div className="relative">
          {/* White textarea card */}
          <div className={cn("bg-card rounded-2xl flex flex-col p-4 shadow-md border min-h-[168px] lg:min-h-[140px] border-slate-300",


      descriptionError && "ring-2 ring-destructive"
      )}>
            <Textarea ref={textareaRef} value={activity} onChange={(e) => {
          setActivity(e.target.value);
          if (descriptionError) setDescriptionError("");
          trackPromptAdded();
        }} onKeyDown={handleKeyDown} placeholder={activity ? placeholder : animatedPlaceholder} disabled={isProcessing || isValidating} className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base bg-transparent flex-shrink-0 min-h-[120px] max-h-[192px] lg:min-h-[100px] lg:max-h-[160px]" />

            {/* File previews */}
            {(photos.length > 0 || plans.length > 0 || docs.length > 0) && <div className="flex gap-2 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                {photos.map((photo) => <div key={photo.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden group">
                    <img src={photo.preview} alt="Site photo" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(photo.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
                {plans.map((plan) => <div key={plan.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex flex-col items-center justify-center group">
                    <FileStack className="h-4 w-4 text-muted-foreground" />
                    <span className="text-[8px] text-muted-foreground truncate max-w-[40px] mt-0.5">
                      {plan.name.replace('.pdf', '')}
                    </span>
                    <button onClick={() => removePlan(plan.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
                {docs.map((doc) => <div key={doc.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex flex-col items-center justify-center group">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-[8px] text-muted-foreground truncate max-w-[40px] mt-0.5">
                      {doc.name.replace(/\.(pdf|docx|xlsx)$/i, '')}
                    </span>
                    <button onClick={() => removeDoc(doc.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
              </div>}

            {/* Send button */}
            <div className="flex justify-end mt-2">
              <Button onClick={handleSubmit} disabled={isProcessing || isValidating || detectedLocation.isLoading} size="icon" className={cn("rounded-full h-10 w-10 shrink-0 aspect-square bg-primary hover:bg-primary/90 text-primary-foreground", (!activity.trim() || !country) && "opacity-50 cursor-not-allowed")}>
                {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Toolbar pills below */}
          <div className="flex items-start gap-2 mt-3 flex-wrap">
            {toolbarContent}
          </div>
        </div>) : (

    /* Default: single card with all controls */
    <>
          <div className={cn("bg-card rounded-2xl overflow-hidden flex flex-col min-h-[180px] p-4 shadow-xl", descriptionError && "ring-2 ring-destructive")}>
            <Textarea ref={textareaRef} value={activity} onChange={(e) => {
          setActivity(e.target.value);
          if (descriptionError) setDescriptionError("");
          trackPromptAdded();
        }} onKeyDown={handleKeyDown} placeholder={activity ? placeholder : animatedPlaceholder} disabled={isProcessing || isValidating} className={cn("resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base bg-transparent flex-shrink-0 min-h-[160px] max-h-[220px]", isFadingIn && "placeholder:animate-fade-in")} />

            {/* File previews */}
            {(photos.length > 0 || plans.length > 0 || docs.length > 0) && <div className="flex gap-2 overflow-x-auto py-2 scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent">
                {photos.map((photo) => <div key={photo.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden group">
                    <img src={photo.preview} alt="Site photo" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(photo.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
                {plans.map((plan) => <div key={plan.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex flex-col items-center justify-center group">
                    <FileStack className="h-4 w-4 text-muted-foreground" />
                    <span className="text-[8px] text-muted-foreground truncate max-w-[40px] mt-0.5">
                      {plan.name.replace('.pdf', '')}
                    </span>
                    <button onClick={() => removePlan(plan.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
                {docs.map((doc) => <div key={doc.id} className="relative flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex flex-col items-center justify-center group">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-[8px] text-muted-foreground truncate max-w-[40px] mt-0.5">
                      {doc.name.replace(/\.(pdf|docx|xlsx)$/i, '')}
                    </span>
                    <button onClick={() => removeDoc(doc.id)} className="absolute top-0.5 right-0.5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" disabled={isProcessing || isValidating}>
                      <X className="h-3 w-3" />
                    </button>
                  </div>)}
              </div>}

            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {toolbarContent}
              <Button onClick={handleSubmit} disabled={isProcessing || isValidating || detectedLocation.isLoading} size="icon" className={cn("rounded-full h-9 w-9 shrink-0 aspect-square bg-primary hover:bg-primary/90 text-primary-foreground", (!activity.trim() || !country) && "opacity-50 cursor-not-allowed")}>
                {isValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </>)
    }
      
      <p className={cn("text-sm mt-2 px-1", descriptionError ? "text-destructive" : "invisible")}>
          {descriptionError || "\u00A0"}
        </p>
      
      {/* Single example prompt with cycling - shown when prompt is too short */}
      {(showExamplePrompts || descriptionError.includes("characters")) && <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between px-1">
            <p className="text-sm text-muted-foreground">Try an example:</p>
            <button
          onClick={handleSeeAnother}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
          
              <RefreshCw className={`h-3 w-3 ${isExampleTransitioning ? 'animate-spin' : ''}`} />
              <span>See another ({currentExampleIndex + 1}/{CLICKABLE_EXAMPLE_PROMPTS.length})</span>
            </button>
          </div>
          <button
        onClick={() => {
          setActivity(CLICKABLE_EXAMPLE_PROMPTS[currentExampleIndex]);
          setDescriptionError("");
        }}
        className={`w-full text-left text-sm px-3 py-2 rounded-lg border border-border/50 
                       hover:border-primary/30 transition-all text-muted-foreground 
                       hover:text-foreground bg-primary-foreground min-h-[52px]
                       ${isExampleTransitioning ? 'opacity-0 translate-y-1' : 'opacity-100 translate-y-0'}`}>
        
            {CLICKABLE_EXAMPLE_PROMPTS[currentExampleIndex]}
          </button>
        </div>}
    </div>;
}